import {OkDocument} from "../../resources/common"
import {BusinessApplication} from "../../resources/application"
import {useNavigate} from "react-router-dom"
import React, {useState} from "react"
import {useModal} from "react-modal-hook"
import {ApplicationResubmitForms} from "./ReevaluatePage"
import ReevaluateBeneficialOwnerForm, {
	ReevaluatedBeneficialOwnerApplicationData,
} from "../../components/ReevaluateApplication/Forms/ReevaluateBeneficialOwnerForm/ReevaluateBeneficialOwnerForm"
import ReevaluateBeneficialOwnerConfirmationModal from "../../components/ReevaluateApplication/ReevaluateConfirmationModal/ReevaluateBusinessApplicationConfirmationModal/ReevaluateBeneficialOwnerConfirmationModal"
import {BeneficialOwnerResource} from "../../resources/beneficialOwner"
import {MetaItemProps} from "../../components/TitleBar/TitleBar"
import ApplicationDetails, {identificationFieldBeforeUpdateRequest} from "./ApplicationDetails"
import Admonition from "../../components/Admonitions/Admonition"

interface ReevaluateBeneficialOwnerPageProps {
	application: OkDocument<BusinessApplication>
	enableReevaluateApplication: boolean
	beneficialOwnerResource: BeneficialOwnerResource
}

function toBeneficialOwnerFormData(
	beneficialOwnerResource?: BeneficialOwnerResource
): ReevaluatedBeneficialOwnerApplicationData | undefined {
	if (
		!beneficialOwnerResource ||
		!beneficialOwnerResource.attributes.address ||
		!beneficialOwnerResource.attributes.dateOfBirth ||
		!beneficialOwnerResource.attributes.email ||
		!beneficialOwnerResource.attributes.phone
	) {
		return undefined
	}

	return {
		fullName: beneficialOwnerResource.attributes.fullName,
		address: beneficialOwnerResource.attributes.address,
		ssn: beneficialOwnerResource.attributes.ssn,
		nationality: beneficialOwnerResource.attributes.nationality,
		passport: beneficialOwnerResource.attributes.passport,
		matriculaConsular: beneficialOwnerResource.attributes.matriculaConsular,
		dateOfBirth: beneficialOwnerResource.attributes.dateOfBirth,
		email: beneficialOwnerResource.attributes.email,
		phone: beneficialOwnerResource.attributes.phone,
		percentage: beneficialOwnerResource.attributes.percentage,
		annualIncome: beneficialOwnerResource.attributes.annualIncome,
		occupation: beneficialOwnerResource.attributes.occupation,
		sourceOfIncome: beneficialOwnerResource.attributes.sourceOfIncome,
	}
}

export default function ReevaluateBeneficialOwnerContainer({
	application,
	enableReevaluateApplication,
	beneficialOwnerResource,
}: ReevaluateBeneficialOwnerPageProps) {
	const navigate = useNavigate()

	const [beneficialOwnerReevaluatedData, setBeneficialOwnerReevaluatedData] =
		useState<ReevaluatedBeneficialOwnerApplicationData>(
			beneficialOwnerResource?.attributes as ReevaluatedBeneficialOwnerApplicationData
		)

	const [showBeneficialOwnerConfirmationModal, closeBeneficialOwnerConfirmationModal] = useModal(
		() => (
			<ReevaluateBeneficialOwnerConfirmationModal
				onSuccess={() => navigate(`/applications/${application.data.id}`)}
				close={closeBeneficialOwnerConfirmationModal}
				application={application.data}
				beneficialOwnerResource={beneficialOwnerResource}
				enableReevaluateApplication={enableReevaluateApplication}
				reevaluatedData={beneficialOwnerReevaluatedData}
			/>
		),
		[beneficialOwnerReevaluatedData]
	)

	const title = application.data.attributes.name
	const meta: MetaItemProps[] = [{text: "Business", icon: "toolbox"}]
	const beneficialOwnerFormData = toBeneficialOwnerFormData(beneficialOwnerResource)

	const form = (
		<ApplicationDetails
			title={title}
			meta={meta}
			application={application}
			formId={ApplicationResubmitForms.beneficialOwnerForm}
		>
			{beneficialOwnerFormData && (
				<ReevaluateBeneficialOwnerForm
					beneficialOwner={beneficialOwnerFormData}
					submitCallback={(values) => {
						const currentValues = {
							ssn: application.data.attributes.officer?.ssn,
							passport: application.data.attributes.officer?.passport,
							matriculaConsular: application.data.attributes.officer?.matriculaConsular,
						}
						const identificationFields = identificationFieldBeforeUpdateRequest({
							currentValues: currentValues,
							updatedValues: values,
						})

						setBeneficialOwnerReevaluatedData({
							...values,
							...identificationFields,
						})
						showBeneficialOwnerConfirmationModal()
					}}
					formId={ApplicationResubmitForms.beneficialOwnerForm}
				/>
			)}
		</ApplicationDetails>
	)

	const error = (
		<Admonition
			type="is-danger"
			title="Could not be able to show resubmit form, please reach administator."
			message={""}
		/>
	)

	if (!beneficialOwnerFormData) {
		return error
	} else {
		return form
	}
}
